import { SearchParamsKeys, SensorFilters } from 'src/enums'

export const sensorReadingOptions = [
  { name: 'Acceleration', value: 'accelerationTime' },
  { name: 'Battery', value: 'batteryTime' },
  { name: 'Dew Point', value: 'dewpointTime' },
  { name: 'Humidity', value: 'humidityTime' },
  { name: 'Light', value: 'lightTime' },
  { name: 'Location Cellular', value: 'locationTime' },
  { name: 'Location GPS', value: 'locationGpsTime' },
  { name: 'Orientation', value: 'orientationTime' },
  { name: 'Pressure', value: 'pressureTime' },
  { name: 'Signal Strength', value: 'signalStrengthTime' },
  { name: 'Temperature', value: 'temperatureTime' }
]

export interface SensorFilterChipDescriptor {
  id: string
  label: string
  // Removal either clears keys or sets explicit values (for params governed by tab defaults)
  clearKeys?: SearchParamsKeys[]
  setValues?: Partial<Record<SearchParamsKeys, string>>
  removable: boolean
}

interface BuildChipsOptions {
  contractorName?: string
  // Params whose value is re-applied by a tab default after removal — their chips render without a remove action
  stickyKeys?: string[]
}

const isOn = (value?: string): boolean => value === 'true'

export const buildSensorFilterChips = (
  queryParams: Record<string, string>,
  activeFilters: SensorFilters[],
  { contractorName, stickyKeys = [] }: BuildChipsOptions = {}
): SensorFilterChipDescriptor[] => {
  const has = (filter: SensorFilters): boolean => activeFilters.includes(filter)
  const chips: SensorFilterChipDescriptor[] = []

  if (has(SensorFilters.LowBattery) && isOn(queryParams[SearchParamsKeys.LowBattery])) {
    chips.push({ id: SearchParamsKeys.LowBattery, label: 'Low battery', clearKeys: [SearchParamsKeys.LowBattery], removable: true })
  }
  if (has(SensorFilters.WeakSignal) && isOn(queryParams[SearchParamsKeys.WeakSignal])) {
    chips.push({ id: SearchParamsKeys.WeakSignal, label: 'Weak signal', clearKeys: [SearchParamsKeys.WeakSignal], removable: true })
  }
  if (has(SensorFilters.LastPingMoreThanHour) && isOn(queryParams[SearchParamsKeys.LastPingMoreThanHour])) {
    chips.push({ id: SearchParamsKeys.LastPingMoreThanHour, label: 'Last ping > 1 hour', clearKeys: [SearchParamsKeys.LastPingMoreThanHour], removable: true })
  }
  if (has(SensorFilters.SyncPeriod) && isOn(queryParams[SearchParamsKeys.SyncPeriodLessThanHour])) {
    chips.push({ id: SearchParamsKeys.SyncPeriodLessThanHour, label: 'Sync period < 1 hour', clearKeys: [SearchParamsKeys.SyncPeriodLessThanHour], removable: true })
  }
  if (has(SensorFilters.LastPingMoreThan) && queryParams[SearchParamsKeys.LastPingMoreThan]) {
    chips.push({
      id: SearchParamsKeys.LastPingMoreThan,
      label: `No ping > ${queryParams[SearchParamsKeys.LastPingMoreThan]} days`,
      clearKeys: [SearchParamsKeys.LastPingMoreThan],
      removable: !stickyKeys.includes(SearchParamsKeys.LastPingMoreThan)
    })
  }
  if (has(SensorFilters.ExcludeStaging) && isOn(queryParams[SearchParamsKeys.ExcludeStaging])) {
    chips.push({
      id: SearchParamsKeys.ExcludeStaging,
      label: 'Staging excluded',
      // The unused-sensor tab re-applies excludeStaging=true by default, so removal must set an explicit false
      setValues: { [SearchParamsKeys.ExcludeStaging]: 'false' },
      removable: true
    })
  }
  if (has(SensorFilters.ReadingsAge) && queryParams[SearchParamsKeys.AgeOfReadingsMoreThanHour]) {
    const names = queryParams[SearchParamsKeys.AgeOfReadingsMoreThanHour]
      .split(',')
      .map((value) => sensorReadingOptions.find((option) => option.value === value)?.name || value)
    chips.push({
      id: SearchParamsKeys.AgeOfReadingsMoreThanHour,
      label: `Readings age > 1h: ${names.join(', ')}`,
      clearKeys: [SearchParamsKeys.AgeOfReadingsMoreThanHour],
      removable: true
    })
  }
  if (has(SensorFilters.AvailabilityInSystem) && queryParams[SearchParamsKeys.IsAvailableInSystem]) {
    chips.push({
      id: SearchParamsKeys.IsAvailableInSystem,
      label: `In system: ${isOn(queryParams[SearchParamsKeys.IsAvailableInSystem]) ? 'Available' : 'Not available'}`,
      clearKeys: [SearchParamsKeys.IsAvailableInSystem],
      removable: true
    })
  }
  const thingspace = queryParams[SearchParamsKeys.IsAvailableInThingspace]
  if (has(SensorFilters.AvailableInThingspace) && thingspace && thingspace !== 'all') {
    chips.push({
      id: SearchParamsKeys.IsAvailableInThingspace,
      label: `Thingspace: ${isOn(thingspace) ? 'Available' : 'Not available'}`,
      clearKeys: [SearchParamsKeys.IsAvailableInThingspace],
      removable: true
    })
  }
  const configuration = queryParams[SearchParamsKeys.CurrentSensorConfiguration]
  if (has(SensorFilters.CurrentSensorConfiguration) && configuration && configuration !== 'all') {
    chips.push({
      id: SearchParamsKeys.CurrentSensorConfiguration,
      label: `Configuration: ${configuration === 'default' ? 'Default' : 'Custom'}`,
      clearKeys: [SearchParamsKeys.CurrentSensorConfiguration],
      removable: true
    })
  }
  if (has(SensorFilters.BleStatus) && queryParams[SearchParamsKeys.IsBleEnabled]) {
    chips.push({
      id: SearchParamsKeys.IsBleEnabled,
      label: `BLE: ${isOn(queryParams[SearchParamsKeys.IsBleEnabled]) ? 'Enabled' : 'Disabled'}`,
      clearKeys: [SearchParamsKeys.IsBleEnabled],
      removable: true
    })
  }
  if (has(SensorFilters.FirmwareVersion) && queryParams[SearchParamsKeys.SensorFirmwareVersion]) {
    chips.push({
      id: SearchParamsKeys.SensorFirmwareVersion,
      label: `Firmware: ${queryParams[SearchParamsKeys.SensorFirmwareVersion]}`,
      clearKeys: [SearchParamsKeys.SensorFirmwareVersion],
      removable: true
    })
  }
  const activationStatus = queryParams[SearchParamsKeys.ActivationStatus]
  if (has(SensorFilters.ActivationStatus) && activationStatus && activationStatus !== 'all') {
    chips.push({
      id: SearchParamsKeys.ActivationStatus,
      label: `Activation: ${activationStatus === 'active' ? 'Active' : 'Deactive'}`,
      clearKeys: [SearchParamsKeys.ActivationStatus],
      removable: true
    })
  }
  if (has(SensorFilters.Contractor) && queryParams[SearchParamsKeys.Contractor]) {
    chips.push({
      id: SearchParamsKeys.Contractor,
      label: `Contractor: ${contractorName || `#${queryParams[SearchParamsKeys.Contractor]}`}`,
      clearKeys: [SearchParamsKeys.Contractor],
      removable: true
    })
  }

  return chips
}

// Every query param this filter form owns — used by "Clear all"
export const getSensorFilterParamKeys = (activeFilters: SensorFilters[]): SearchParamsKeys[] => {
  const keys: SearchParamsKeys[] = []
  const add = (filter: SensorFilters, ...paramKeys: SearchParamsKeys[]) => {
    if (activeFilters.includes(filter)) keys.push(...paramKeys)
  }
  add(SensorFilters.LowBattery, SearchParamsKeys.LowBattery)
  add(SensorFilters.WeakSignal, SearchParamsKeys.WeakSignal)
  add(SensorFilters.LastPingMoreThanHour, SearchParamsKeys.LastPingMoreThanHour)
  add(SensorFilters.SyncPeriod, SearchParamsKeys.SyncPeriodLessThanHour)
  add(SensorFilters.LastPingMoreThan, SearchParamsKeys.LastPingMoreThan)
  add(SensorFilters.ExcludeStaging, SearchParamsKeys.ExcludeStaging)
  add(SensorFilters.ReadingsAge, SearchParamsKeys.AgeOfReadingsMoreThanHour)
  add(SensorFilters.AvailabilityInSystem, SearchParamsKeys.IsAvailableInSystem)
  add(SensorFilters.AvailableInThingspace, SearchParamsKeys.IsAvailableInThingspace)
  add(SensorFilters.CurrentSensorConfiguration, SearchParamsKeys.CurrentSensorConfiguration)
  add(SensorFilters.BleStatus, SearchParamsKeys.IsBleEnabled)
  add(SensorFilters.FirmwareVersion, SearchParamsKeys.SensorFirmwareVersion)
  add(SensorFilters.ActivationStatus, SearchParamsKeys.ActivationStatus)
  add(SensorFilters.Contractor, SearchParamsKeys.Contractor)
  add(SensorFilters.OrderNumber, SearchParamsKeys.OrderNumber, SearchParamsKeys.OrderPart)
  add(SensorFilters.OrderDate, SearchParamsKeys.OrderDateFrom, SearchParamsKeys.OrderDateTo)
  return keys
}
